import React, { useState } from 'react'
import { omMessage, OmRadio, OmRadioGroup } from '@owma/kit'
import xCircleRed22SVG from 'assets/images/exclamation-circle-red-22.svg'
import { confirmBase, confirmDelete } from 'utils/ui'
import { TodoItem } from '../modules/TodoItem'
import { TFunction } from 'i18next'
import {
  deleteTodoItem,
  deleteRecurrenceTodoItem,
  deleteTodoItems,
  deleteCompletedTodos,
  DeleteResult,
} from './todoDeletePure'

export { deleteTodoItem, deleteRecurrenceTodoItem, deleteTodoItems, deleteCompletedTodos }
export type { DeleteResult }

/**
 * 공통 Todo 삭제 처리 유틸리티
 * TodoEditor와 TodoList에서 공유하는 삭제 로직을 통합
 */

interface HandleTodoDeleteParams {
  todo: TodoItem
  t: TFunction
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
}

/** confirmBase(okButtonProps: any) 로 전달되는 최소 버튼 속성. */
interface ConfirmButtonProps {
  className?: string
  style?: React.CSSProperties
  danger?: boolean
  disabled?: boolean
}

interface ConfirmRecurrenceDeleteOptions {
  title: string
  content?: string
  okText: string
  cancelText: string
  onOk?: (selectedOption: number) => void | Promise<void>
  onCancel?: () => void
  okButtonProps?: ConfirmButtonProps
  cancelButtonProps?: ConfirmButtonProps
}

/**
 * Todo 삭제 확인 다이얼로그를 표시하고 삭제를 처리합니다.
 *
 * @param {HandleTodoDeleteParams} params - 삭제 처리에 필요한 파라미터들
 * @returns {Promise<void>}
 */
export const handleTodoDelete = async ({
  todo,
  t,
  onSuccess,
  onError,
  onCancel,
}: HandleTodoDeleteParams): Promise<void> => {
  // todo.url이 없으면 새 항목이므로 삭제할 것이 없음
  if (!todo.url) {
    if (onSuccess) {
      onSuccess()
    }
    return
  }

  if (todo.recurrenceRule) {
    confirmRecurrenceDelete(
      {
        title: t('todo.editor.deleteAllTasksTitle'),
        content: t('todo.editor.deleteRecurrence'),
        okText: t('common.button.delete'),
        cancelText: t('common.button.cancel'),
        onOk: async (index: number) => {
          try {
            await deleteRecurrenceTodoItem(todo, index === 1, t)
            if (onSuccess) {
              onSuccess()
            }
          } catch (error) {
            console.error('Delete failed:', error)
            omMessage.error(t('todo.editor.deleteFailed'))
            if (onError) {
              onError(error as Error)
            }
          }
        },
        onCancel: () => {
          if (onCancel) {
            onCancel()
          }
        },
      },
      t,
    )
  } else {
    confirmDelete({
      title: t('todo.editor.deleteTitle'),
      content: t('todo.editor.deleteContent'),
      okText: t('common.button.delete'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          await deleteTodoItem(todo, t)
          if (onSuccess) {
            onSuccess()
          }
        } catch (error) {
          console.error('Delete failed:', error)
          omMessage.error(t('todo.editor.deleteFailed'))
          if (onError) {
            onError(error as Error)
          }
        }
      },
      onCancel: () => {
        if (onCancel) {
          onCancel()
        }
      },
    })
  }
}

export const confirmRecurrenceDelete = (
  options: ConfirmRecurrenceDeleteOptions,
  t: TFunction,
): ReturnType<typeof confirmBase> => {
  let selectedOption = 0

  const RecurrenceDeleteContent = (): React.ReactElement => {
    const [selected, setSelected] = useState<number>(0)

    const handleOptionChange = (value: number | string): void => {
      const next = Number(value)
      selectedOption = next
      setSelected(next)
    }

    return (
      <div style={{ marginTop: '16px' }}>
        {options.content && <div style={{ marginBottom: '16px' }}>{options.content}</div>}
        <OmRadioGroup onChange={handleOptionChange} value={selected} style={{ width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <OmRadio value={0}>{t('todo.editor.deleteOnlyThisOccurrence')}</OmRadio>
            <OmRadio value={1}>{t('todo.editor.deleteAllFutureOccurrences')}</OmRadio>
          </div>
        </OmRadioGroup>
      </div>
    )
  }

  return confirmBase({
    maskTransitionName: 'maskTransitionName',
    icon: <img src={xCircleRed22SVG} alt="error" />,
    okText: options.okText || 'Delete',
    cancelText: options.cancelText || 'Cancel',
    title: options.title,
    content: <RecurrenceDeleteContent />,
    okButtonProps: {
      className: 'type1 h38',
      style: { minWidth: '100px' },
      ...(options != null ? options.okButtonProps : null),
    },
    cancelButtonProps: {
      className: 'h38',
      style: { minWidth: '100px' },
      ...(options != null ? options.cancelButtonProps : null),
    },
    onOk: () => {
      if (options.onOk) {
        options.onOk(selectedOption)
      }
    },
    onCancel: () => {
      if (options.onCancel) {
        options.onCancel()
      }
    },
  })
}
